import { usePresence } from '@/hooks/usePresence';
import type { ChatContactUser } from '@/components/Messenger/sidebar/ChatSidebar';
import { getUserDisplayName } from '@/components/Messenger/sidebar/ChatSidebar';

type MessengerPickerContactRowMultiProps = {
  mode: 'multi';
  user: ChatContactUser;
  checked: boolean;
  disabled?: boolean;
  onToggle: (userId: string) => void;
};

type MessengerPickerContactRowSingleProps = {
  mode: 'single';
  user: ChatContactUser;
  disabled?: boolean;
  busy?: boolean;
  onPick: (user: ChatContactUser) => void;
  /** Модалка пересылки: одна колонка текста с группами, индикаторы в строке имени */
  forwardLayout?: boolean;
};

export type MessengerPickerContactRowProps =
  | MessengerPickerContactRowMultiProps
  | MessengerPickerContactRowSingleProps;

/**
 * Строка контакта в модалке выбора получателя: чекбокс (группа) или одиночный выбор (пересылка).
 */
export function MessengerPickerContactRow(props: MessengerPickerContactRowProps) {
  const { user } = props;
  const presence = usePresence(user.id);
  const displayName = getUserDisplayName(user);
  const roleLine = user.role?.trim() ? (
    <span className="chatSidebar__itemRole">{user.role.trim()}</span>
  ) : null;

  const textBlock = (
    <span className="chatSidebar__itemContent">
      <span className="chatSidebar__itemLabelRow">
        <span className="chatSidebar__itemLabel messengerContactPicker__rowName">{displayName}</span>
      </span>
      {roleLine}
    </span>
  );

  if (props.mode === 'multi') {
    const { checked, disabled, onToggle } = props;
    return (
      <label className="messengerContactPicker__row">
        <span className="messengerContactPicker__rowLead">
          <input
            type="checkbox"
            checked={checked}
            onChange={() => onToggle(user.id)}
            disabled={disabled}
            className="messengerContactPicker__rowCheck"
          />
        </span>
        {textBlock}
      </label>
    );
  }

  const { disabled, busy, onPick, forwardLayout } = props;
  if (forwardLayout) {
    return (
      <button
        type="button"
        className="messengerContactPicker__row messengerContactPicker__row_forwardLayout"
        disabled={disabled}
        aria-busy={busy || undefined}
        onClick={() => onPick(user)}
      >
        <span className="chatSidebar__itemContent">
          <span className="chatSidebar__itemLabelRow">
            {busy ? (
              <span className="chatSidebar__searchSpinner" aria-hidden />
            ) : presence.online ? (
              <span className="chatSidebar__onlineDot" aria-hidden />
            ) : null}
            <span className="chatSidebar__itemLabel messengerContactPicker__rowName">{displayName}</span>
          </span>
          {roleLine}
        </span>
      </button>
    );
  }

  return (
    <button
      type="button"
      className="messengerContactPicker__row"
      disabled={disabled}
      aria-busy={busy || undefined}
      onClick={() => onPick(user)}
    >
      <span className="messengerContactPicker__rowLead" aria-hidden>
        {busy ? (
          <span className="chatSidebar__searchSpinner" />
        ) : (
          presence.online && <span className="chatSidebar__onlineDot" />
        )}
      </span>
      {textBlock}
    </button>
  );
}
